import { Target, Users, TrendingUp, BarChart3, Lightbulb, Zap } from "lucide-react";
import { motion } from "framer-motion";

// 1. REAL TAGS from your "Beyond Product Management" & Project Sections
const expertiseStack = [
  "Consumer Research", "Mentorship", "Public Speaking", "B2B SaaS", "AI/ML", "Python", "Climate Tech", "Agile", "Roadmapping", "A/B Testing"
];

// 2. REAL CONTENT from your "Core Product Skills" section
const bentoItems = [
  {
    id: 1,
    colSpan: "md:col-span-2", // Wide Card
    title: "Product Strategy",
    // Exact text from video 00:06
    desc: "Defining product vision, roadmap, and go-to-market strategies that align with business objectives and user needs.",
    icon: Target,
    bg: "bg-blue-50",
    text: "text-blue-600"
  },
  {
    id: 2,
    colSpan: "md:col-span-1", // Square Card
    title: "Data-Driven Decisions",
    // Exact text from video 00:07
    desc: "Leveraging analytics and market research to inform product decisions.",
    icon: BarChart3,
    bg: "bg-indigo-50",
    text: "text-indigo-600"
  },
  {
    id: 3,
    colSpan: "md:col-span-1", // Square Card
    title: "Innovation & Ideation",
    // Exact text from video 00:07
    desc: "Identifying market opportunities and translating them into innovative product features.",
    icon: Lightbulb,
    bg: "bg-amber-50",
    text: "text-amber-600"
  },
  {
    id: 4,
    colSpan: "md:col-span-2", // Wide Card
    title: "Cross-Functional Leadership",
    // Exact text from video 00:06
    desc: "Leading diverse teams across engineering, design, and marketing to deliver cohesive product experiences.",
    icon: Users,
    bg: "bg-purple-50",
    text: "text-purple-600"
  },
  {
    id: 5,
    colSpan: "md:col-span-3", // Full Width Card
    title: "Growth & Optimization",
    // Exact text from video 00:06
    desc: "Driving product growth through data analysis, A/B testing, and continuous iteration based on user feedback.",
    icon: TrendingUp,
    bg: "bg-emerald-50",
    text: "text-emerald-600"
  }
];

const Skills = () => {
  return (
    <section id="skills" className="py-32 px-4 md:px-8 bg-slate-50 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-20 text-center max-w-3xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, filter: "blur(0px)" }}
            viewport={{ once: false }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold text-slate-900 tracking-tight mb-6"
          >
            Core Product Skills.
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ delay: 0.2 }}
            className="text-slate-500 text-lg"
          >
            I bridge technical teams and strategy to create products that truly matter.
          </motion.p>
        </div>

        {/* --- THE BENTO GRID (Real Data) --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
          {bentoItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: false }} // Replay on scroll
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`${item.colSpan} group relative overflow-hidden bg-white rounded-3xl p-8 border border-slate-200 shadow-sm hover:shadow-2xl transition-all duration-500`}
            >
              {/* Hover Gradient Bloom */}
              <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-3xl ${item.bg.replace('bg-', 'bg-')}`} style={{ backgroundColor: item.text.replace('text-', '') }} />
              
              <div className="relative z-10 flex flex-col h-full">
                {/* Icon Container */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 transition-transform duration-500 group-hover:scale-110 ${item.bg}`}>
                  <item.icon className={`w-6 h-6 ${item.text}`} />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-slate-700 transition-colors">
                  {item.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* --- INFINITE MARQUEE (Real Extra Tags) --- */}
        <div className="relative pt-10 border-t border-slate-200">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-50 to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-50 to-transparent z-10" />
          
          <div className="flex overflow-hidden">
            {/* We duplicate the list to make the loop seamless */}
            <MarqueeGroup stack={expertiseStack} />
            <MarqueeGroup stack={expertiseStack} />
            <MarqueeGroup stack={expertiseStack} />
            <MarqueeGroup stack={expertiseStack} />
          </div>
        </div>

      </div>
    </section>
  );
};

const MarqueeGroup = ({ stack }) => (
  <motion.div 
    className="flex gap-8 px-4"
    animate={{ x: "-100%" }}
    transition={{ 
      duration: 40, // Slower for readability
      repeat: Infinity, 
      ease: "linear" 
    }}
  >
    {stack.map((tool, i) => (
      <div key={i} className="flex items-center gap-2 whitespace-nowrap opacity-60 hover:opacity-100 transition-opacity cursor-default">
        <Zap className="w-4 h-4 text-slate-400" />
        <span className="text-xl font-bold text-slate-700">{tool}</span>
      </div>
    ))}
  </motion.div>
);

export default Skills;